import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Image, Typography } from 'antd';
import { useSetState } from 'ahooks';
import Permission from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';

import { userStoryList, type UserStory } from '@/services/user-story';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

const PERMISSION = 'Dramas_Activity-1011_User-Stories';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

const formatTime = (value?: string | null) => {
    if (!value) return '-';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(value);
};

const PageUserStories: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<UserStory[]>([]);

    const fetchList = useCallback(
        async (page = 1, pageSize = 20) => {
            setLoading(true);
            try {
                const response = await userStoryList({ page, size: pageSize });
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const rows = Array.isArray(data.data) ? (data.data as UserStory[]) : [];
                setDataSource(rows);

                const meta = data.meta as { page?: number; size?: number; total?: number } | null | undefined;
                if (meta) {
                    setPagination({
                        current: Number(meta.page) > 0 ? Number(meta.page) : page,
                        pageSize: Number(meta.size) > 0 ? Number(meta.size) : pageSize,
                        total: meta.total ?? rows.length,
                    });
                } else {
                    setPagination({
                        current: page,
                        pageSize,
                        total: rows.length,
                    });
                }
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch list');
            } finally {
                setLoading(false);
            }
        },
        [message, setPagination],
    );

    useEffect(() => {
        void fetchList(1, DEFAULT_PAGINATION.pageSize as number);
    }, [fetchList]);

    const columns = useMemo<ProColumns<UserStory>[]>(
        () => [
            {
                title: '#',
                width: 64,
                align: 'center',
                render: (_, __, index) => {
                    const current = Number(pagination.current) || 1;
                    const pageSize = Number(pagination.pageSize) || 20;
                    return (current - 1) * pageSize + index + 1;
                },
            },
            {
                title: <FormattedMessage id="userStories.columns.uid" />,
                width: 180,
                render: (_, record) => {
                    const uid = record.user_id || '-';
                    return uid === '-' ? (
                        '-'
                    ) : (
                        <Typography.Text copyable={{ text: uid }} style={{ fontSize: 12 }}>
                            {uid}
                        </Typography.Text>
                    );
                },
            },
            {
                title: <FormattedMessage id="userStories.columns.content" />,
                dataIndex: 'content',
                ellipsis: true,
                render: (_, record) => {
                    const text = record.content?.trim() || '';
                    if (!text) return '-';
                    return (
                        <Typography.Paragraph
                            style={{ marginBottom: 0, maxWidth: 400, whiteSpace: 'pre-wrap' }}
                            ellipsis={{
                                rows: 3,
                                expandable: 'collapsible',
                                symbol: (expanded) =>
                                    formatMessage({
                                        id: expanded ? 'userStories.collapse' : 'userStories.expand',
                                    }),
                            }}
                        >
                            {text}
                        </Typography.Paragraph>
                    );
                },
            },
            {
                title: <FormattedMessage id="userStories.columns.image" />,
                width: 100,
                render: (_, record) => {
                    const url = record.image_url?.trim();
                    if (!url) {
                        return (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage id="userStories.noImage" />
                            </Typography.Text>
                        );
                    }
                    return (
                        <Image
                            src={url}
                            alt=""
                            width={60}
                            height={60}
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                    );
                },
            },
            {
                title: <FormattedMessage id="userStories.columns.submittedAt" />,
                width: 180,
                render: (_, record) => formatTime(record.create_at),
            },
        ],
        [formatMessage, pagination.current, pagination.pageSize],
    );

    const total = Number(pagination.total) || 0;

    return (
        <Permission permission={PERMISSION}>
            <ProCard
                title={<FormattedMessage id="userStories.title" />}
                extra={
                    <Typography.Text type="secondary">
                        <FormattedMessage id="userStories.count" values={{ count: total }} />
                    </Typography.Text>
                }
            >
                <AdminProTable<UserStory>
                    rowKey={(row) => String(row.id)}
                    loading={loading}
                    search={false}
                    options={false}
                    toolBarRender={false}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            void fetchList(page, pageSize);
                        },
                    }}
                    locale={{
                        emptyText: (
                            <div style={{ padding: '48px 16px', textAlign: 'center' }}>
                                <Typography.Title level={5} type="secondary">
                                    <FormattedMessage id="userStories.empty.title" />
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    <FormattedMessage id="userStories.empty.desc" />
                                </Typography.Text>
                            </div>
                        ),
                    }}
                />
            </ProCard>
        </Permission>
    );
};

export default PageUserStories;
